import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { WebOSApp, AppNavigationContextType } from "../types/webos";

const AppNavigationContext = createContext<
  AppNavigationContextType | undefined
>(undefined);

interface AppNavigationProviderProps {
  children: ReactNode;
}

export function AppNavigationProvider({
  children,
}: AppNavigationProviderProps) {
  const [currentApp, setCurrentApp] = useState<WebOSApp | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const launchApp = useCallback(
    (app: WebOSApp) => {
      setCurrentApp(app);
      navigate(`/apps/${app.id}`);
    },
    [navigate]
  );

  const closeApp = useCallback(() => {
    setCurrentApp(null);
    navigate("/");
  }, [navigate]);

  const goHome = useCallback(() => {
    setCurrentApp(null);
    navigate("/");
  }, [navigate]);

  // Update current app based on route changes
  useEffect(() => {
    const pathMatch = location.pathname.match(/^\/apps\/([^\/]+)/);
    if (!pathMatch && currentApp) {
      setCurrentApp(null);
    }
  }, [location.pathname, currentApp]);

  const value: AppNavigationContextType = {
    currentApp,
    launchApp,
    closeApp,
    goHome,
    addToRecent: () => {}, // No-op function
    recentApps: [], // Empty array
  };

  return (
    <AppNavigationContext.Provider value={value}>
      {children}
    </AppNavigationContext.Provider>
  );
}

export function useAppNavigation(): AppNavigationContextType {
  const context = useContext(AppNavigationContext);
  if (context === undefined) {
    throw new Error(
      "useAppNavigation must be used within an AppNavigationProvider"
    );
  }
  return context;
}
